import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/match-v2-diag`;

const PROBE_PATHS = [
  '/api/health',
  '/api/auth/me',
  '/api/messages/unread-count',
  '/api/opportunities-v2?limit=1',
  '/api/match-v2/status',
];

const fmtBytes = (b) => {
  if (b === 'unavailable' || b == null) return 'unavailable';
  const n = Number(b);
  if (Number.isNaN(n)) return String(b);
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-mono text-slate-800 text-right break-all">
      {value === null || value === undefined ? 'unavailable' : value}
    </span>
  </div>
);

export default function MatchV2Diagnostics() {
  const [data, setData] = useState(null);
  const [seeker, setSeeker] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [probes, setProbes] = useState({});
  const pollRef = useRef(null);
  const probeRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/status`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setMsg(e?.response?.status === 403 ? 'Forbidden (admin only)' : 'Failed to load status');
    }
  }, []);

  const runProbes = useCallback(async () => {
    const out = {};
    await Promise.all(PROBE_PATHS.map(async (p) => {
      const t0 = performance.now();
      try {
        const res = await fetch(`${BACKEND_URL}${p}`, { credentials: 'include' });
        out[p] = { ms: Math.round(performance.now() - t0), status: res.status, ok: res.ok };
      } catch (e) {
        out[p] = { ms: Math.round(performance.now() - t0), status: 'ERR', ok: false };
      }
    }));
    setProbes(out);
  }, []);

  useEffect(() => {
    load();
    runProbes();
    pollRef.current = setInterval(load, 3000);
    probeRef.current = setInterval(runProbes, 5000);
    return () => { clearInterval(pollRef.current); clearInterval(probeRef.current); };
  }, [load, runProbes]);

  const act = async (fn) => { setBusy(true); setMsg(''); try { await fn(); await load(); } catch (e) { setMsg(e?.response?.data?.detail || e?.message || 'error'); } finally { setBusy(false); } };

  const start = () => act(async () => {
    const r = await axios.post(`${API}/start`, { seeker: seeker.trim() }, { withCredentials: true });
    setMsg(`Started session ${r.data.session_id}`);
  });
  const stop = () => act(async () => { await axios.post(`${API}/stop`, {}, { withCredentials: true }); setMsg('Stop requested (halts at next batch boundary).'); });
  const resume = (sid) => act(async () => { await axios.post(`${API}/resume`, { session_id: sid }, { withCredentials: true }); setMsg('Resume requested.'); });

  const s = data?.session;
  const rt = data?.runtime;
  const cpu = rt?.cgroup_cpu_stat || {};
  const mem = rt?.cgroup_memory || {};
  const analysis = data?.instance_analysis;
  const batches = (s?.batches || []).slice().reverse();
  const cls = analysis?.classification;

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="match-v2-diag-page">
      <h1 className="text-2xl font-bold text-slate-900 mb-1">Match V2 Diagnostics</h1>
      <p className="text-sm text-slate-500 mb-4">Admin-only. Runs ONE controlled materialization while public Match stays governed by the kill switch.</p>

      {(cls === 'multiple_instances_observed' || cls === 'boot_id_differs_unconfirmed') && (
        <div className={`mb-4 rounded-md border px-4 py-3 text-sm ${cls === 'multiple_instances_observed' ? 'border-red-300 bg-red-50 text-red-800' : 'border-amber-300 bg-amber-50 text-amber-800'}`} data-testid="diag-instance-banner">
          <div className="font-bold">
            {cls === 'multiple_instances_observed' ? 'MULTIPLE INSTANCE OBSERVED' : 'BOOT ID DIFFERS (not confirmed as a restart)'}
          </div>
          <div>current boot id: <span className="font-mono">{analysis.current_boot_id}</span> · started <span className="font-mono">{String(analysis.current_process_start)}</span></div>
          <div>session boot id: <span className="font-mono">{analysis.session_boot_id}</span> · last telemetry <span className="font-mono">{String(analysis.session_last_telemetry_at)}</span></div>
          {analysis.overlap_proven && (
            <div className="mt-1">current process started BEFORE another distinct writer's last telemetry → concurrent instances, not a sequential restart.</div>
          )}
          <div className="mt-1">distinct boot ids observed: <span className="font-mono">{(analysis.distinct_boot_ids_observed || []).join(', ') || '—'}</span></div>
          {(analysis.instances_observed || []).length > 0 && (
            <table className="w-full mt-2 text-xs font-mono">
              <thead><tr className="text-left"><th className="pr-3">host</th><th className="pr-3">boot</th><th className="pr-3">pid</th><th className="pr-3">start</th><th>cgroup</th></tr></thead>
              <tbody>
                {analysis.instances_observed.map((i) => (
                  <tr key={i.key}><td className="pr-3">{i.hostname}</td><td className="pr-3">{String(i.boot_id).slice(0, 10)}</td><td className="pr-3">{i.pid}</td><td className="pr-3">{String(i.process_start)}</td><td className="break-all">{String(i.cgroup_path)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Controls + public switch */}
        <div className="rounded-lg border border-slate-200 p-4">
          <h2 className="font-semibold text-slate-800 mb-2">Controls</h2>
          <div className="mb-3">
            <span className="text-sm text-slate-500">Public Match V2 materialization: </span>
            <span className={`text-sm font-bold ${data?.public_materialization_enabled ? 'text-amber-600' : 'text-green-700'}`} data-testid="diag-public-switch">
              {data ? (data.public_materialization_enabled ? 'ENABLED' : 'DISABLED') : '—'}
            </span>
          </div>
          <div className="flex gap-2 mb-2">
            <input
              data-testid="diag-seeker-input"
              value={seeker}
              onChange={(e) => setSeeker(e.target.value)}
              placeholder="seeker email or user_id"
              className="flex-1 h-9 rounded border border-slate-300 px-2 text-sm"
            />
            <button data-testid="diag-start-btn" disabled={busy || !seeker.trim()} onClick={start}
              className="h-9 px-3 rounded bg-slate-900 text-white text-sm disabled:opacity-40">START</button>
          </div>
          <div className="flex gap-2">
            <button data-testid="diag-stop-btn" disabled={busy} onClick={stop}
              className="h-9 px-3 rounded bg-amber-600 text-white text-sm disabled:opacity-40">STOP</button>
            {s?.session_id && (s.status === 'interrupted' || s.status === 'stopped') && (
              <button data-testid="diag-resume-btn" disabled={busy} onClick={() => resume(s.session_id)}
                className="h-9 px-3 rounded bg-blue-600 text-white text-sm disabled:opacity-40">RESUME</button>
            )}
          </div>
          {msg && <p className="mt-2 text-xs text-slate-600" data-testid="diag-msg">{msg}</p>}
        </div>

        {/* Session + progress */}
        <div className="rounded-lg border border-slate-200 p-4" data-testid="diag-session-panel">
          <h2 className="font-semibold text-slate-800 mb-2">Session</h2>
          <Row label="status" value={<span data-testid="diag-session-status">{s?.status || 'idle'}</span>} />
          <Row label="seeker" value={s?.seeker_email || s?.seeker_id || '—'} />
          <Row label="session_id" value={s?.session_id || '—'} />
          <Row label="scored / total" value={`${s?.scored ?? '—'} / ${s?.total ?? '—'}`} />
          <Row label="missing" value={s?.missing ?? '—'} />
          <Row label="current batch" value={s?.current_batch ?? '—'} />
          <Row label="attempts" value={s?.attempts ?? '—'} />
          <Row label="heartbeat" value={s?.heartbeat_at || '—'} />
          <Row label="batch size / pause" value={`${s?.batch_size ?? '—'} / ${s?.pause_ms ?? '—'}ms`} />
        </div>

        {/* Runtime / process */}
        <div className="rounded-lg border border-slate-200 p-4">
          <h2 className="font-semibold text-slate-800 mb-2">Runtime / Process</h2>
          <Row label="pid" value={rt?.pid} />
          <Row label="boot id" value={rt?.boot_id} />
          <Row label="process start" value={rt?.process_start_time} />
          <Row label="process RSS" value={fmtBytes(rt?.process_rss_bytes)} />
          <Row label="event-loop lag (ms, recent)" value={(rt?.event_loop_lag_ms_recent || []).slice(-8).join(', ') || '—'} />
        </div>

        {/* Container / cgroup */}
        <div className="rounded-lg border border-slate-200 p-4">
          <h2 className="font-semibold text-slate-800 mb-2">Container / cgroup</h2>
          <Row label="memory.current" value={fmtBytes(mem.current)} />
          <Row label="memory.max" value={fmtBytes(mem.max)} />
          <Row label="memory.events oom" value={mem.oom} />
          <Row label="memory.events oom_kill" value={mem.oom_kill} />
          <Row label="cpu usage_usec" value={cpu.usage_usec} />
          <Row label="cpu nr_periods" value={cpu.nr_periods} />
          <Row label="cpu nr_throttled" value={cpu.nr_throttled} />
          <Row label="cpu throttled_usec" value={cpu.throttled_usec} />
        </div>
      </div>

      {/* Browser-side origin probes */}
      <div className="rounded-lg border border-slate-200 p-4 mt-6" data-testid="diag-probes">
        <h2 className="font-semibold text-slate-800 mb-2">Browser origin probes (~5s)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {PROBE_PATHS.map((p) => (
            <div key={p} className="text-xs font-mono flex justify-between border border-slate-100 rounded px-2 py-1">
              <span className="truncate">{p}</span>
              <span className={probes[p]?.ok ? 'text-green-700' : 'text-red-600'}>
                {probes[p] ? `${probes[p].status} ${probes[p].ms}ms` : '…'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-batch telemetry */}
      <div className="rounded-lg border border-slate-200 p-4 mt-6" data-testid="diag-batches">
        <h2 className="font-semibold text-slate-800 mb-2">Per-batch telemetry (latest first)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-1 pr-3">#</th><th className="pr-3">jobs</th><th className="pr-3">score ms</th>
                <th className="pr-3">write ms</th><th className="pr-3">pause ms</th><th className="pr-3">cycle ms</th>
                <th className="pr-3">cpu Δusec</th><th className="pr-3">thr Δperiods</th><th className="pr-3">thr Δusec</th>
                <th className="pr-3">rss</th><th className="pr-3">loop lag ms</th>
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 && <tr><td colSpan={11} className="py-2 text-slate-400">no batches yet</td></tr>}
              {batches.map((b) => (
                <tr key={b.n} className="border-b border-slate-100">
                  <td className="py-1 pr-3">{b.n}</td><td className="pr-3">{b.jobs}</td><td className="pr-3">{b.score_ms}</td>
                  <td className="pr-3">{b.write_ms}</td><td className="pr-3">{b.pause_ms}</td><td className="pr-3">{b.cycle_ms}</td>
                  <td className="pr-3">{String(b.cpu_usage_delta_usec)}</td><td className="pr-3">{String(b.throttled_periods_delta)}</td>
                  <td className="pr-3">{String(b.throttled_usec_delta)}</td><td className="pr-3">{fmtBytes(b.rss_bytes)}</td>
                  <td className="pr-3">{String(b.loop_lag_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
